import React, { useEffect, useState } from 'react';
import { Button, Card, CardBody, CardTitle, Col, Container, Form, Input, Label, Row, FormFeedback } from "reactstrap";
import * as yup from "yup";
import { useFormik } from "formik";
import Breadcrumb from 'Components/UI/Breadcrumb';
import Select from "react-select";
import { getProducts } from 'helpers/API Calls/Operations/Products';
import { getPackages } from 'helpers/API Calls/Operations/packages';
import { getAllCategories } from 'helpers/API Calls/Operations/category';
import { getDiscountTypes, getDiscountValidityOptions, getDiscountScopes, updateDiscount, getDiscountById } from 'helpers/API Calls/Operations/discounts';
import Spinners from "Components/UI/Spinner";
import { useNavigate, useParams } from 'react-router-dom';
import { STATUS_OPTIONS } from 'pages/Categories/data';

interface DiscountFormValues {
  name: string;
  code: string;
  discountScope: { label: string; value: number, modal?: string | null } | null;
  validityType: { label: string; value: number, slug: string } | null;
  startDate: string;
  endDate: string;
  discountType: { label: string; value: number, slug: string } | null;
  discountAmount: number;
  selectedItems: { label: string; value: number } | null;
  isUnlimited: boolean;
  usageLimit: number;
  status: { label: string; value: string } | null;
}

// Helper function to format ISO date to YYYY-MM-DD
const formatISOToDate = (isoDate: string | null): string => {
  if (!isoDate) return "";
  return new Date(isoDate).toISOString().split('T')[0];
};

function EditDiscountCode() {

  const navigate = useNavigate();
  const { id } = useParams();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isOptionLoading, setIsOptionLoading] = useState<boolean>(false);
  const [dropdownOptions, setDropdownOptions] = useState<{typeOptions: any[]; validityOptions: any[]; scopeOptions: any[]; itemsOptions: any[];}>(
  {
    typeOptions: [],
    validityOptions: [],
    scopeOptions: [],
    itemsOptions: [],
  });

  const today = new Date().toISOString().split('T')[0];

  const formik = useFormik<DiscountFormValues>({
    initialValues: {
      name: '',
      code: '',
      discountScope: null,
      validityType: null,
      startDate: '',
      endDate: '',
      discountType: null,
      discountAmount: 0,
      selectedItems: null,
      isUnlimited: true,
      usageLimit: 0,
      status: null, 
    },
    validationSchema: yup.object().shape({
      name: yup.string().required('Please enter discount name'),
      code: yup.string().required('Please enter discount code'),
      discountScope: yup.object().shape({
        label: yup.string().required(),
        value: yup.number().required(),
      }).nullable().required('Please select discount scope'),
      validityType: yup.object().shape({
        label: yup.string().required(),
        value: yup.number().required(),
        slug: yup.string().required(),
      }).nullable().required('Please select validity type'),
      startDate: yup.string().when('validityType', {
        is: (val: any) => val?.slug === 'specific_booking_dates' || val?.slug === 'specific_hire_dates',
        then: (schema) => schema
          .required('Start date is required')
      }),
      endDate: yup.string().when(['validityType', 'startDate'], {
        is: (validityType: any, startDate: string) => 
          (validityType?.slug === 'specific_booking_dates' || validityType?.slug === 'specific_hire_dates') && startDate,
        then: (schema) => schema
          .required('End date is required')
          .test('is-greater-or-equal', 'End date must be greater than or equal to start date', function(value) {
            const { startDate } = this.parent;
            if (!value || !startDate) return true;
            return new Date(value) >= new Date(startDate);
          })
      }),
      discountType: yup.object().shape({
        label: yup.string().required(),
        value: yup.number().required(),
        slug: yup.string().required()
      }).nullable().required('Please select discount type'),
      discountAmount: yup.number()
        .required('Please enter discount amount')
        .min(1, 'Discount amount must be greater than 0')
        .when('discountType', {
          is: (val: any) => val?.slug === 'percentage',
          then: (schema) => schema.max(100, 'Percentage discount cannot exceed 100%')
        }),
      selectedItems: yup.object().shape({
        label: yup.string().required(),
        value: yup.number().required()
      }).nullable().when('discountScope', {
        is: (val: any) => val?.modal !== null,
        then: (schema) => schema.required('Please select an item')
      }),
      usageLimit: yup.number().when('isUnlimited', {
        is: false,
        then: (schema) => schema.required('Please enter usage limit').min(1, 'Usage limit must be at least 1')
      }),
      status : yup.object().shape({label: yup.string().required(), value: yup.string().required(),}).nullable().required('Please select status'),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      const reqData = {
        name: values.name,
        code: values.code,

        discount_type_id: values.discountType?.value,
        discount_type_amount: values.discountAmount,
        
        discount_scope_id: values.discountScope?.value,
        discount_scope: {
          specific_scope_id: values.discountScope?.modal ? values.selectedItems?.value : null
        },

        discount_validity_id: values.validityType?.value,
        discount_validity: {
          from_date: values.validityType?.slug === 'all_time' ? null : values.startDate,
          to_date: values.validityType?.slug === 'all_time' ? null : values.endDate
        },

        frequency: values.isUnlimited ? -1 : values.usageLimit,

        status: values?.status?.value,
      }
      if (!id) return;
      const response = await updateDiscount(Number(id), reqData);
      if (response.success) {
        navigate('/discounts');
      }
      setSubmitting(false);
    }
  });

  // load data
  useEffect(() => {
    const getData = async () => {
      setIsLoading(true); 

      if (!id) {
        console.error("No ID provided for editing discount code.");
        setIsLoading(false);
        return;
      }

      let typeOptions = [];
      let validityOptions = [];
      let scopeOptions = [];

      // Get discount data first
      const discountData = await getDiscountById(Number(id));
      if (discountData.success) {
        const data = discountData.data;
        const status_label = data.status === 'active' ? 'Active' : 'Inactive';
        formik.setValues({
          name: data?.name,
          code: data.code,
          discountType: {
            label: data.discount_type.discount_type,
            value: data.discount_type.id,
            slug: data.discount_type.slug
          },
          discountAmount: data.discount_type_amount,
          discountScope: {
            label: data.discount_scope.discount_scope,
            value: data.discount_scope.id,
            modal: data.discount_scope.model_name
          },
          selectedItems: data.specific_scope_id ? {
            label: data?.specific_scope_data?.name || data.specific_scope_data?.product_name || data.specific_scope_data?.category_name,
            value: data.specific_scope_data?.id
          } : null,
          validityType: {
            label: data.discount_validity.discount_validity,
            value: data.discount_validity.id,
            slug: data.discount_validity.slug
          },
          startDate: formatISOToDate(data.from_date),
          endDate: formatISOToDate(data.to_date),
          isUnlimited: data.frequency === -1,
          usageLimit: data.frequency === -1 ? 0 : data.frequency,
          status: {label: status_label, value: data.status},
        });

      }

      const types = await getDiscountTypes();
      if (types.success) {
        typeOptions = types.data.map((type: any) => ({ 
          label: type.discount_type, 
          value: Number(type.id),
          slug: type.slug 
        }));
      }

      const validityOpts = await getDiscountValidityOptions();
      if (validityOpts.success) {
        validityOptions = validityOpts.data.map((opt: any) => ({ label: opt.discount_validity, value: Number(opt.id), slug: opt.slug}));
      }
      
      const scopes = await getDiscountScopes();
      if (scopes.success) {
        scopeOptions = scopes.data.map((scope: any) => ({
          label: scope.discount_scope,
          value: Number(scope.id),
          modal: scope.model_name
        }));
      }

      // Only load item options if model_name exists
      loadItemOptions(discountData?.data?.discount_scope.model_name || '');

      setDropdownOptions({
        typeOptions,
        validityOptions,
        scopeOptions,
        itemsOptions: []
      });

      setIsLoading(false);
    }
    getData();
  }, [id]);
  
  // Effect to load items when scope changes
  useEffect(() => {
    if (formik.values.discountScope?.modal) {
      loadItemOptions(formik.values.discountScope.modal);
    }
  }, [formik.values.discountScope]);
  // load items based on selected scope
  const loadItemOptions = async (scopeType: string) => {
    if (!scopeType) return;
    
    setIsOptionLoading(true);
    try {
      let items: { label: string; value: number }[] = [];
      
      switch (scopeType) {
        case 'products':
          const products = await getProducts('limit=1000');
          if (products.success) {
            items = products.data.items.map((product: any) => ({
              label: product.product_name,
              value: product.id
            }));
          }
          break;
          
        case 'packages':
          const packages = await getPackages('limit=1000');
          if (packages.success) {
            items = packages.data.items.map((pkg: any) => ({
              label: pkg.package_name,
              value: pkg.id,
            }));
          }
          break;
          
        case 'categories':
          const categories = await getAllCategories();
          if (categories.success) {
            items = categories.data.items.map((category: any) => ({
              label: category.category_name,
              value: category.id
            }));
          }
          break;
      }
      
      setDropdownOptions(prev => ({
        ...prev,
        itemsOptions: items
      }));
    } catch (error) {
      console.error('Error loading items:', error);
    }
    setIsOptionLoading(false);
  };
  
  // Add scroll to error effect
  useEffect(() => {
    if (Object.keys(formik.errors).length > 0 && formik.isSubmitting) {
      const firstErrorKey = Object.keys(formik.errors)[0];
      const element = document.getElementById(firstErrorKey);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
      }
    }
  }, [formik.errors, formik.isSubmitting]);

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb items={[{ label: "Discounts", link: "/discounts" }, { label: 'Edit' }]} />
        {isLoading ? <div className="position-relative vh-100"><Spinners/></div>
        :
        <Card>
          <CardBody>
            <Form onSubmit={formik.handleSubmit} autoComplete="off">
              {/* Basic Information */}
              <div className="mb-4">
                <CardTitle tag="h4" className='mb-4'>Basic Information</CardTitle>
                <Row>
                  <Col sm="6">
                    <div className="mb-3">
                      <Label htmlFor="name">Name <span className="text-danger">*</span></Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        placeholder="Enter discount name"
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        invalid={formik.touched.name && formik.errors.name ? true : false}
                      />
                      {formik.errors.name && formik.touched.name && (
                        <FormFeedback type="invalid">{formik.errors.name}</FormFeedback>
                      )}
                    </div>
                  </Col>
                  <Col sm="6">
                    <div className="mb-3">
                      <Label htmlFor="code">Code <span className="text-danger">*</span></Label>
                      <Input
                        id="code"
                        name="code"
                        type="text"
                        placeholder="Enter discount code"
                        value={formik.values.code}
                        onChange={formik.handleChange}
                        invalid={formik.touched.code && formik.errors.code ? true : false}
                      />
                      {formik.errors.code && formik.touched.code && (
                        <FormFeedback type="invalid">{formik.errors.code}</FormFeedback>
                      )}
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col sm="6">
                    <div className="mb-3">
                      <Label htmlFor="discountType">Discount Type <span className="text-danger">*</span></Label>
                      <Select
                        id="discountType"
                        name="discountType"
                        placeholder="Select discount type"
                        options={dropdownOptions.typeOptions}
                        value={formik.values.discountType}
                        onChange={(value) => formik.setFieldValue("discountType", value)}
                        className={formik.touched.discountType && formik.errors.discountType ? "is-invalid" : ""}
                      />
                      {formik.touched.discountType && formik.errors.discountType && (
                        <div className="invalid-feedback d-block">
                          {typeof formik.errors.discountType === 'string' && formik.errors.discountType}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col sm="6">
                    <div className="mb-3">
                      <Label htmlFor="discountAmount">
                        Discount Amount 
                        {formik.values.discountType?.slug === 'percentage' ? ' (%)' : ''} 
                        <span className="text-danger">*</span>
                      </Label>
                      <Input
                        id="discountAmount"
                        name="discountAmount"
                        type="number"
                        step={formik.values.discountType?.slug === 'percentage' ? '1' : '0.01'}
                        placeholder={`Enter discount ${formik.values.discountType?.slug === 'percentage' ? 'percentage' : 'amount'}`}
                        value={formik.values.discountAmount}
                        onChange={formik.handleChange}
                        invalid={formik.touched.discountAmount && formik.errors.discountAmount ? true : false}
                      />
                      {formik.errors.discountAmount && formik.touched.discountAmount && (
                        <FormFeedback type="invalid">{formik.errors.discountAmount}</FormFeedback>
                      )}
                    </div>
                  </Col>
                </Row>
              </div>

              {/* Configuration */}
              <div className="mb-4">
                <CardTitle tag="h4" className='mb-4'>Configuration</CardTitle>
                <Row>
                  <Col sm="6">
                    <div className="mb-3">
                      <Label htmlFor="discountScope">Discount Scope <span className="text-danger">*</span></Label>
                      <Select
                        id="discountScope"
                        name="discountScope"
                        placeholder="Select discount scope"
                        options={dropdownOptions.scopeOptions}
                        value={formik.values.discountScope}
                        onChange={(value) => {
                          formik.setFieldValue("discountScope", value);
                          // Always reset selected items when scope changes
                          formik.setFieldValue("selectedItems", null);
                        }}
                        className={formik.touched.discountScope && formik.errors.discountScope ? "is-invalid" : ""}
                      />
                      {formik.touched.discountScope && formik.errors.discountScope && (
                        <div className="invalid-feedback d-block">
                          {typeof formik.errors.discountScope === 'string' && formik.errors.discountScope}
                        </div>
                      )}
                    </div>
                    {(formik.values.discountScope && formik.values.discountScope?.modal) && (
                      <div className="mb-3">
                        <Label htmlFor="selectedItems">Select Items <span className="text-danger">*</span></Label>
                        <Select
                          id="selectedItems"
                          name="selectedItems"
                          placeholder="Select items"
                          options={dropdownOptions.itemsOptions}
                          value={formik.values.selectedItems}
                          onChange={(value) => formik.setFieldValue("selectedItems", value)}
                          className={formik.touched.selectedItems && formik.errors.selectedItems ? "is-invalid" : ""}
                          isLoading={isOptionLoading}
                          isDisabled={isOptionLoading}
                        />
                        {formik.touched.selectedItems && formik.errors.selectedItems && !isOptionLoading && (
                          <div className="invalid-feedback d-block">
                            {typeof formik.errors.selectedItems === 'string' && formik.errors.selectedItems}
                          </div>
                        )}
                      </div>
                    )}

                    {!formik.values.isUnlimited && (
                      <div className="mb-3">
                        <Label htmlFor="usageLimit">Usage Limit <span className="text-danger">*</span></Label>
                        <Input
                          id="usageLimit"
                          name="usageLimit"
                          type="number"
                          min="1"
                          placeholder="Enter usage limit"
                          value={formik.values.usageLimit}
                          onChange={formik.handleChange}
                          invalid={formik.touched.usageLimit && formik.errors.usageLimit ? true : false}
                        />
                        {formik.errors.usageLimit && formik.touched.usageLimit && (
                          <FormFeedback type="invalid">{formik.errors.usageLimit}</FormFeedback>
                        )}
                      </div>
                    )}
                    <div className="mb-3">
                      <div className="form-check mb-3">
                        <Input
                          type="checkbox"
                          className="form-check-input"
                          id="isUnlimited"
                          name="isUnlimited"
                          checked={formik.values.isUnlimited}
                          onChange={(e) => {
                            formik.setFieldValue("isUnlimited", e.target.checked);
                            if (e.target.checked) {
                              formik.setFieldValue("usageLimit", 0);
                            }
                          }}
                        />
                        <Label className="form-check-label" htmlFor="isUnlimited">
                          Unlimited Usage
                        </Label>
                      </div>
                    </div>
                  </Col>
                  <Col sm="6">
                    <div className="mb-4">
                      <Label className="form-label">Status</Label>
                      <Select
                        name="status"
                        placeholder="Select Status"
                        options={STATUS_OPTIONS}
                        value={formik.values.status}
                        onChange={(value) => formik.setFieldValue("status", value)}
                        onBlur={formik.handleBlur}
                        className={
                          formik.touched.status && formik.errors.status
                            ? "is-invalid"
                            : ""
                        }
                      />
                      {formik.touched.status && formik.errors.status ? (
                        <FormFeedback type="invalid">{formik.errors.status || formik.errors.status}</FormFeedback>
                      ) : null}
                    </div>

                    <div className="mb-3">
                      <Label htmlFor="validityType">Validity Type <span className="text-danger">*</span></Label>
                      <Select
                        id="validityType"
                        name="validityType"
                        placeholder="Select validity type"
                        options={dropdownOptions.validityOptions}
                        value={formik.values.validityType}
                        onChange={(value) => formik.setFieldValue("validityType", value)}
                        className={formik.touched.validityType && formik.errors.validityType ? "is-invalid" : ""}
                      />
                      {formik.touched.validityType && formik.errors.validityType && (
                        <div className="invalid-feedback d-block">
                          {typeof formik.errors.validityType === 'string' && formik.errors.validityType}
                        </div>
                      )}
                    </div>

                  {(formik.values?.validityType?.slug === 'specific_booking_dates' || formik.values?.validityType?.slug === 'specific_hire_dates') && (
                    <>
                    <div className="mb-3">
                      <Label htmlFor="startDate">Start Date <span className="text-danger">*</span></Label>
                      <Input
                        id="startDate"
                        name="startDate"
                        type="date"
                        min={today}
                        value={formik.values.startDate}
                        onChange={formik.handleChange}
                        invalid={formik.touched.startDate && formik.errors.startDate ? true : false}
                      />
                      {formik.errors.startDate && formik.touched.startDate && (
                        <FormFeedback type="invalid">{formik.errors.startDate}</FormFeedback>
                      )}
                    </div>
                  
                    <div className="mb-3">
                      <Label htmlFor="endDate">End Date <span className="text-danger">*</span></Label>
                      <Input
                        id="endDate"
                        name="endDate"
                        type="date"
                        min={formik.values.startDate || today}
                        value={formik.values.endDate}
                        onChange={formik.handleChange}
                        invalid={formik.touched.endDate && formik.errors.endDate ? true : false}
                      />
                      {formik.errors.endDate && formik.touched.endDate && (
                        <FormFeedback type="invalid">{formik.errors.endDate}</FormFeedback>
                      )}
                    </div>
                    </>
                  )}
                  </Col>
                </Row>
              </div>
              
              <div className="d-flex flex-wrap gap-2 mt-4 justify-content-end">
                <Button type="submit" color="primary" disabled={formik.isSubmitting}>
                  {formik.isSubmitting ? 'Updating...' : 'Update Discount Code'}
                </Button>
              </div>
            </Form>
          </CardBody>
        </Card>
        }
      </Container>
    </div>
  );
}

export default EditDiscountCode;
